"use client"

import { useGetEventsProvider } from "openapi/api/endpoints"
import type { Event } from "openapi/api/model"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import { useCalendarBooking } from "@/hooks/use-calendar-booking"
import { useEventNoteCreation } from "@/hooks/use-event-note-creation"
import { toCalendarAttachments } from "@/lib/calendar-attachments"
import { resolveInviteMessageForSubmission, supportsCustomInviteMessage } from "@/lib/calendar-invite-message"

type SubmitMode = "create" | "edit"

type SubmitCalendarEventArgs = {
  mode: SubmitMode
  data: CalendarEventFormDataV2
  selectedEvent?: Event | null
}

const toUtcIsoFromLocal = (date: Date, time24: string): string => {
  const [hh, mm] = time24.split(":").map(Number)
  const d = new Date(date)
  d.setHours(hh, mm, 0, 0)
  return d.toISOString()
}

export function useCalendarEventSubmit() {
  const { data: eventsProvider } = useGetEventsProvider()
  const { createEvent, updateEvent, deleteEvent } = useCalendarBooking()
  const { createEventNote } = useEventNoteCreation()

  const allowAttachments = eventsProvider?.is_azure === true || eventsProvider?.provider === "azure"
  const supportsInviteMessage = supportsCustomInviteMessage(eventsProvider)

  const submitEvent = async ({
    mode,
    data,
    selectedEvent,
  }: SubmitCalendarEventArgs) => {
    const startISO = toUtcIsoFromLocal(data.date, data.start_time)
    const endISO = toUtcIsoFromLocal(data.date, data.end_time)
    const attachments = allowAttachments ? await toCalendarAttachments(data.attachments) : undefined
    const candidateId = data.candidate_id
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (mode === "create") {
      const inviteMessage = resolveInviteMessageForSubmission({
        mode,
        sendInvites: data.send_invites,
        supportsInviteMessage,
        includeInviteMessage: data.include_invite_message,
        inviteMessage: data.invite_message,
      })

      const payload = {
        title: data.title,
        event_type: data.event_type,
        start_at: startISO,
        end_at: endISO,
        location: data.location ?? undefined,
        notes: data.notes ?? undefined,
        job_id: data.job_id || undefined,
        candidate_id: candidateId,
        send_invites: data.send_invites,
        invitee_emails: data.send_invites ? data.invitee_emails ?? [] : [],
        time_zone: timeZone,
        attachments,
        ...(inviteMessage !== undefined ? { invite_message: inviteMessage } : {}),
      }

      const response = await createEvent(payload)

      await createEventNote({
        linkType: data.linkType,
        jobId: data.job_id,
        candidateId,
        notes: data.notes,
      })

      return response
    }

    if (!selectedEvent?.id) {
      throw new Error("Selected event is required for updates.")
    }

    const inviteMessage = resolveInviteMessageForSubmission({
      mode,
      sendInvites: true,
      supportsInviteMessage,
      includeInviteMessage: data.include_invite_message,
      inviteMessage: data.invite_message,
    })

    return updateEvent(selectedEvent.id, {
      title: data.title,
      event_type: data.event_type,
      start_at: startISO,
      end_at: endISO,
      location: data.location ?? null,
      notes: data.notes ?? null,
      job_id: data.job_id || null,
      candidate_id: candidateId ?? null,
      invitee_emails: data.invitee_emails ?? [],
      time_zone: timeZone,
      attachments,
      remove_attachment_ids:
        allowAttachments && (data.remove_attachment_ids?.length ?? 0) > 0
          ? data.remove_attachment_ids
          : undefined,
      ...(inviteMessage !== undefined ? { invite_message: inviteMessage } : {}),
    })
  }

  return {
    allowAttachments,
    supportsInviteMessage,
    submitEvent,
    deleteEvent,
  }
}
